import type { Editor } from "@tiptap/react";

import { decodeHtmlEntities, escapeHtml, escapeHtmlAttr } from "@tellescope/utils";

import { NEW_EDITOR_STATE_SENTINEL } from "../editorStateSentinel";
import { findTokens } from "../tokens/recognize";
import { anchorsToTokens } from "./anchorLinks";
import { TEMPLATE_TOKEN_ATTR } from "./TemplateTokenNode";

/**
 * Body serialization in both directions. Reconstruction depends only on `html`/`message`, so these
 * helpers are the bridge between the TipTap document and the stored `MessageTemplate` body. On save
 * the token chips collapse back to their exact raw strings; on load raw tokens are wrapped into
 * chip nodes. `editorState` carries only the sentinel — never reconstruction data.
 */

const TOKEN_SPAN_RE = new RegExp(`<span[^>]*${TEMPLATE_TOKEN_ATTR}="([^"]*)"[^>]*>\\s*</span>`, "g");

/** Collapse serialized token spans back to the bare raw token string the resolver expects. */
export const restoreRawTokens = (html: string): string =>
  html.replace(TOKEN_SPAN_RE, (_match, escaped: string) => decodeHtmlEntities(escaped));

/** Wrap every recognized token in `html` into a chip-node span so `setContent` rebuilds chips. */
export const wrapTokensForEditor = (html: string): string =>
  [...findTokens(html)].reverse().reduce((result, { start, end, token }) => {
    const span = `<span ${TEMPLATE_TOKEN_ATTR}="${escapeHtmlAttr(token.raw)}"></span>`;
    return result.slice(0, start) + span + result.slice(end);
  }, html);

/** Stored HTML (with bare tokens) → editor content (with chip-node spans) for `setContent`. */
export const htmlToEditorContent = (html: string | undefined): string => wrapTokensForEditor(html ?? "");

/**
 * Editor document → stored `html` (HTML with bare `{{…}}` / `{url}[text]` tokens). Anchors are
 * normalized to `{url}[text]` link markup (contract §3) unless `preserveAnchorTags` is set, in which
 * case literal `<a>` tags are kept. (§6.1 audit: TipTap separates blocks with `<p>…</p>`, not block-
 * level `<br/>`, so there is no `<br/>` doubling to strip; only genuine hard breaks emit `<br>`.)
 */
export const editorToHtml = (editor: Editor, { preserveAnchorTags = false } = {}): string => {
  const html = preserveAnchorTags ? editor.getHTML() : anchorsToTokens(editor.getHTML());
  return restoreRawTokens(html);
};

/** A list item's plaintext prefix: `• ` for unordered lists, `N. ` (1-based) for ordered. */
const listItemPrefix = (listTag: string, index: number): string => (listTag === "OL" ? `${index + 1}. ` : "• ");

/**
 * Serialize a parsed-HTML node to plaintext (contract §6): block elements are newline-separated,
 * `<br>` is a newline, list items are bullet/number prefixed, and `<a>` has already been collapsed to
 * `{url}[text]` token text upstream. Inline styling carries no plaintext and is dropped.
 */
const nodeToMessage = (node: Node): string => {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? "";
    // A whitespace-only text node that spans a line break is insignificant HTML source indentation
    // (the newlines/spaces authored between block tags), not authored content — drop it so pasted,
    // pretty-printed HTML doesn't explode into runs of blank lines. Whitespace without a newline is
    // a real inline gap between words and is kept.
    if (/^\s*$/.test(text)) return /\n/.test(text) ? "" : text;
    return text;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const element = node;
  if (!(element instanceof Element)) return "";
  if (element.tagName === "BR") return "\n";

  if (element.tagName === "UL" || element.tagName === "OL") {
    return [...element.children]
      .reduce<string[]>((items, child) => {
        if (child.tagName !== "LI") return items;
        return [...items, `${listItemPrefix(element.tagName, items.length)}${childrenToMessage(child).trim()}`];
      }, [])
      .join("\n");
  }

  const text = childrenToMessage(element);
  // Block-level elements stand on their own line; inline elements contribute their text as-is.
  return BLOCK_TAGS.has(element.tagName) ? `\n${text}\n` : text;
};

const childrenToMessage = (node: Node): string => [...node.childNodes].map(nodeToMessage).join("");

/** Block tags that force a line break around their content when flattened to plaintext. */
const BLOCK_TAGS = new Set(["P", "DIV", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE", "PRE", "LI"]);

/** Collapse the runs of blank lines that block-boundary newlines produce into single separators. */
const normalizeMessageWhitespace = (message: string): string =>
  message
    // Strip horizontal whitespace hugging a newline (leading indentation / trailing spaces on a line)
    // before collapsing blank-line runs, so an indented line like a CTA doesn't keep its source indent.
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

/**
 * Stored/authored `html` → stored `message` (plaintext with raw tokens, contract §6). Links are
 * ALWAYS represented as `{url}[text]` — SMS reads only `message`, so the URL and its tracking must
 * survive there even when the `html` keeps literal anchors. Shared by the rich editor and the raw
 * HTML field so both plaintext derivations are identical (links, list bullets, block newlines).
 */
export const htmlToMessage = (html: string): string => {
  const normalized = restoreRawTokens(anchorsToTokens(html));
  const doc = new DOMParser().parseFromString(normalized, "text/html");
  return normalizeMessageWhitespace(childrenToMessage(doc.body));
};

/** Editor document → stored `message`. See {@link htmlToMessage}. */
export const editorToMessage = (editor: Editor): string => htmlToMessage(editor.getHTML());

/**
 * Stored `message` (plaintext with raw tokens / `{url}[text]` links) → stored `html`, used on save
 * when no selected channel supports HTML (SMS-only), so the HTML body still mirrors the plaintext the
 * user actually authored. HTML-special chars are escaped and newlines become `<br>`; raw tokens and
 * link markup carry no HTML-special chars, so they pass through literally. The plaintext inverse of
 * {@link htmlToMessage}.
 */
export const messageToHtml = (message: string): string => escapeHtml(message).replace(/\n/g, "<br>");

/** The value to persist into `editorState` on every save (old-UI interop sentinel only). */
export const serializeEditorState = (): string => NEW_EDITOR_STATE_SENTINEL;
